import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ChangeEvent,
  type FocusEvent,
  type MouseEvent,
} from "react";
import { CalendarIcon } from "lucide-react";

import { useDateFormat } from "@/shared/hooks/use-date-format";
import { useLanguage } from "@/shared/hooks/use-language";
import type { DateFormat } from "@/shared/lib/date-format";
import { formatDateInput, maskDateInput, parseDateInput } from "@/shared/lib/date-utils";
import { Button } from "./button";
import { FormField, type FormFieldProps } from "./form-field";

export interface DateFieldProps
  extends Omit<FormFieldProps, "value" | "onChange" | "type" | "min" | "max"> {
  /** ISO `YYYY-MM-DD`, or `""` when no date is set. */
  value: string;
  /**
   * Receives ISO `YYYY-MM-DD` once the entry is a real calendar date, `""` otherwise.
   * Emits only when the parsed value differs from what was last emitted, so typing all
   * eight digits of a date fires one change, not eight — except while a parent `error`
   * is displayed, when any keystroke re-fires, including one that reparses to the same
   * value (retyping the same digit). This matches every other `FormField` in this
   * codebase, which clears its own error inside `onChange` on any change, not only a
   * value-changing one.
   */
  onChange: (iso: string) => void;
  /** ISO bounds, forwarded to the calendar picker only — never enforced on typed entry. */
  pickerMin?: string;
  pickerMax?: string;
}

const DIGITS_IN_DATE = 8;

const textFromIso = (iso: string, format: DateFormat): string =>
  iso ? formatDateInput(iso, format) : "";

// How many digits sit to the left of `caret` in `value` — separators don't count, so this
// is stable across a mask that inserts or removes them.
const digitIndexAt = (value: string, caret: number): number =>
  value.slice(0, caret).replace(/\D/g, "").length;

// The position in `masked` that follows exactly `digitIndex` digits — the inverse of
// `digitIndexAt`, read against the string the mask just produced.
const caretAfterDigits = (masked: string, digitIndex: number): number => {
  let seen = 0;
  for (let i = 0; i < masked.length; i++) {
    if (seen === digitIndex) return i;
    if (/\d/.test(masked[i])) seen++;
  }
  return masked.length;
};

// `showPicker` is absent on Safari < 16, iOS < 16 and Firefox < 101. Read once per field
// instance rather than on every render — the answer cannot change during a session — and
// guarded for a module that may load before `window` exists.
const supportsDatePicker = () =>
  typeof window !== "undefined"
  && typeof window.HTMLInputElement !== "undefined"
  && typeof window.HTMLInputElement.prototype.showPicker === "function";

/**
 * A date input that shows and accepts the user's selected date format while carrying ISO
 * `YYYY-MM-DD` in state. The calendar button opens the browser's own picker on a hidden
 * native input, so the visible field never renders in the browser's locale; the button is
 * omitted entirely where the browser has no `showPicker`, rather than falling back to a
 * click on a hidden input that does nothing there.
 *
 * `value` is authoritative for what the field displays, with one exception this field
 * cannot resolve by itself: a parent that sets `value` to the exact string the field last
 * emitted (typically `""`, since an unparsed entry emits `""` once, not on every further
 * keystroke — see `onChange` above) causes no prop change, so nothing re-derives `text`
 * and the unparsed characters stay on screen. The two Clear buttons in this repo that must
 * override an unparsed entry this way — Access History's "Clear filters" and the investor
 * transaction filter sheet's "Clear filters" — remount the field with a fresh `key`
 * instead of relying on `value` alone; see `access/index.tsx`'s `historyResetCount` and
 * `investors/detail.tsx`'s `filterResetCount`.
 */
export const DateField = ({
  id,
  label,
  value,
  onChange,
  pickerMin,
  pickerMax,
  onBlur,
  onFocus,
  onMouseUp,
  onMouseDown,
  disabled,
  ...rest
}: DateFieldProps) => {
  const { dateFormat } = useDateFormat();
  const { t } = useLanguage();
  const [text, setText] = useState(() => textFromIso(value, dateFormat));
  const [blurredInvalid, setBlurredInvalid] = useState(false);
  const [pickerSupported] = useState(supportsDatePicker);
  // Bumped on every keystroke so the caret-restore layout effect below always re-runs,
  // even on the keystroke whose re-masked text comes out identical to what was already
  // on screen (backspacing the separator right after a segment: the digits are unchanged,
  // so the mask reproduces the same string). `text` alone as the effect's dependency
  // would then never change, React would bail out of re-rendering this component, and the
  // pending caret position would never be applied — leaving the caret wherever the
  // browser's own edit left it, which is not where the mask says it belongs.
  const [caretVersion, setCaretVersion] = useState(0);
  const nativeRef = useRef<HTMLInputElement>(null);
  const textInputRef = useRef<HTMLInputElement>(null);
  const pendingCaretRef = useRef<number | null>(null);
  const selectOnMouseUpRef = useRef(false);
  // Set just before a programmatic `.focus()` call that must not select the text — a
  // calendar pick returns focus to the visible input with the caret left where it is,
  // not selected the way a user's own click or Tab would leave it. `handleFocus` reads
  // and clears it on the very focus event it is guarding.
  const suppressFocusSelectRef = useRef(false);
  // The last ISO value this field itself emitted (or was told to treat as current, on a
  // parent-driven `value` push or a picked date). `handleTextChange` emits again only when
  // the newly parsed value differs from this — see `onChange`'s own doc comment.
  const lastEmittedRef = useRef(value);

  // A format change always redraws `text` from `value` in the new format, dropping any
  // in-progress partial entry — the same digits would parse under the wrong
  // separators/segment order otherwise. Keyed on `dateFormat` alone so this never fires
  // from typing, only from the format preference itself changing.
  useEffect(() => {
    setText(textFromIso(value, dateFormat));
    setBlurredInvalid(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFormat]);

  // A `value` push from the parent re-derives `text` only when the field isn't already
  // showing it. `handleTextChange` already set `text` (and called `onChange` with the same
  // parse) inside the same event, so on an ordinary keystroke `value` arrives already equal
  // to what `text` parses to and this is a no-op; it only fires for a value the field did
  // not itself just produce — a picked date, or a parent-driven reset to a genuinely
  // different value.
  useEffect(() => {
    if (value === (parseDateInput(text, dateFormat) ?? "")) return;
    setText(textFromIso(value, dateFormat));
    setBlurredInvalid(false);
    lastEmittedRef.current = value;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useLayoutEffect(() => {
    const caret = pendingCaretRef.current;
    if (caret == null) return;
    pendingCaretRef.current = null;
    textInputRef.current?.setSelectionRange(caret, caret);
    // caretVersion is read only to force this effect to re-run on a keystroke whose
    // masked text happens to equal the text already on screen — see its declaration.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, caretVersion]);

  const handleTextChange = (raw: string, event: ChangeEvent<HTMLInputElement>) => {
    const caret = event.target.selectionStart ?? raw.length;
    const digitIndex = digitIndexAt(raw, caret);
    const masked = maskDateInput(raw, dateFormat);
    pendingCaretRef.current = caretAfterDigits(masked, digitIndex);
    setCaretVersion((count) => count + 1);
    setText(masked);
    setBlurredInvalid(false);
    const parsed = parseDateInput(masked, dateFormat) ?? "";
    // While a parent `error` is showing, any keystroke re-fires — including one that
    // reparses to the same value — so the parent's own `onChange`, which clears its
    // error on any change, runs immediately instead of waiting for the entry to change
    // value.
    if (rest.error || parsed !== lastEmittedRef.current) {
      lastEmittedRef.current = parsed;
      onChange(parsed);
    }
  };

  const handlePicked = (iso: string) => {
    setText(textFromIso(iso, dateFormat));
    setBlurredInvalid(false);
    lastEmittedRef.current = iso;
    onChange(iso);
    // Return focus to the visible field rather than leaving it on the hidden,
    // `aria-hidden` native input the picker opened on — resting focus on an element
    // excluded from the accessibility tree is a trap for a keyboard or screen-reader
    // user tabbing next.
    suppressFocusSelectRef.current = true;
    textInputRef.current?.focus();
  };

  const openPicker = () => {
    nativeRef.current?.showPicker();
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    setBlurredInvalid(text !== "" && parseDateInput(text, dateFormat) === null);
    selectOnMouseUpRef.current = false;
    onBlur?.(event);
  };

  // Selecting the whole value on focus means a click into a pre-filled field replaces it
  // by typing, rather than inserting into the middle of the old date — and the same select
  // fires for a keyboard Tab into the field, so a Tab lands with the value ready to
  // overwrite too. The browser's own mouseup handling re-collapses that selection to the
  // click point immediately afterward; suppressing that default is only correct for the
  // very click that carried focus in. `handleMouseDown` is what decides that, by checking
  // whether the field was already focused *before* this mousedown — not `handleFocus`,
  // since focus also fires from a Tab, which never has a mouseup to suppress and must not
  // arm one for a later click. A click on an already-focused field is left alone and
  // places the caret normally. The one other source of a `focus` event, `handlePicked`'s
  // programmatic `.focus()` after a calendar pick, arms `suppressFocusSelectRef` first —
  // that focus must land with the caret where it is, not with the whole value selected.
  const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
    if (suppressFocusSelectRef.current) {
      suppressFocusSelectRef.current = false;
    } else {
      event.target.select();
    }
    onFocus?.(event);
  };

  const handleMouseDown = (event: MouseEvent<HTMLInputElement>) => {
    selectOnMouseUpRef.current = document.activeElement !== event.currentTarget;
    onMouseDown?.(event);
  };

  const handleMouseUp = (event: MouseEvent<HTMLInputElement>) => {
    if (selectOnMouseUpRef.current) {
      selectOnMouseUpRef.current = false;
      event.preventDefault();
    }
    onMouseUp?.(event);
  };

  const digitCount = text.replace(/\D/g, "").length;
  const completeButInvalid =
    digitCount === DIGITS_IN_DATE && parseDateInput(text, dateFormat) === null;
  const ownError = completeButInvalid || blurredInvalid ? t("dateField.invalid") : null;

  // The placeholder is the only visible cue to the expected shape, and it disappears the
  // moment text is entered. The format rides along for assistive tech instead, as a
  // visually-hidden paragraph this component renders itself and wires through
  // `aria-describedby` — never as a visible line, so a `DateField` cell keeps the same
  // height as its siblings (a status `<select>`, a same-row time `FormField`) whether or
  // not the format cue is present. A caller-supplied `hint` still renders visibly through
  // `FormField`'s own mechanism, unchanged; the format cue is additional, not appended to
  // it. `FormField` computes its own `aria-describedby` from `error`/`hint`, but that
  // computation only sees one of the two ids this field needs, so it is overridden below
  // with the composed list — `error`/`hint`'s id (matching what `FormField` would have
  // picked on its own) plus the format id, always.
  const finalError = rest.error || ownError;
  const formatHintId = `${id}-format-hint`;
  const primaryDescribedById = finalError ? `${id}-error` : rest.hint ? `${id}-hint` : undefined;
  const describedBy = [primaryDescribedById, formatHintId].filter(Boolean).join(" ");
  const formatHint = t("dateField.formatHint").replace("{format}", dateFormat);

  return (
    <>
      <FormField
        {...rest}
        ref={textInputRef}
        id={id}
        label={label}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder={dateFormat}
        value={text}
        onChange={handleTextChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        disabled={disabled}
        error={finalError}
        hint={rest.hint}
        aria-describedby={describedBy}
        rightSlot={
          pickerSupported ? (
            <>
              <input
                ref={nativeRef}
                type="date"
                id={`${id}-native`}
                className="sr-only"
                tabIndex={-1}
                aria-hidden="true"
                value={value}
                min={pickerMin}
                max={pickerMax}
                disabled={disabled}
                onChange={(event) => handlePicked(event.target.value)}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-muted-foreground hover:text-foreground"
                aria-label={t("dateField.openCalendar")}
                data-testid={`${id}-picker`}
                disabled={disabled}
                onClick={openPicker}
              >
                <CalendarIcon />
              </Button>
            </>
          ) : undefined
        }
      />
      {/* Unconditional — the format cue must reach assistive tech even where the browser
          has no `showPicker` and `rightSlot` renders nothing. */}
      <p id={formatHintId} className="sr-only">
        {formatHint}
      </p>
    </>
  );
};
